/**
 * Handoff Manager API
 * API client for the lead-to-agent handoff service
 */

import { createCloudRunAxios, getCloudRunServiceUrl } from './axios';
import {
  HandoffStatus,
  HotSignals,
  HandoffLane,
  ChannelOfOrigin,
  HandoffLocation,
  HandoffQueueResponse,
  HandoffStatusResponse,
  HandoffStats,
  ClaimHandoffRequest,
  ClaimHandoffResponse,
  DeclineHandoffRequest,
  DeclineHandoffResponse,
  EvaluateHandoffRequest,
  EvaluateHandoffResponse,
  HandoffHealthResponse,
  HandoffSSEEvent,
} from '../types/handoff.types';
import { openHandoffAlertStream } from './handoffAlertsStream';
import { errorReason, HandoffApiErrorLike } from '../utils/handoffApiError';
import {
  EMPTY_STATUS_COUNTS,
  HandoffStatusCounts,
  normalizeHandoffStatus,
  normalizeStatusCounts,
} from '../utils/handoffStatus';

const remiApi = createCloudRunAxios('remi-api');
// The mobile-handoff SSE stream lives on the `remi` service. Its stream-token
// mint endpoint (SPEC-2157) needs a normal Bearer header, so it goes through an
// auth-interceptor axios instance (the EventSource itself can't send headers).
const remi = createCloudRunAxios('remi');

type AxiosLike = HandoffApiErrorLike;

// SSE raw data from backend (snake_case)
/**
 * Health check for the Handoff Manager API
 * @returns Health status of the service
 */
export const checkHandoffHealth = async (): Promise<HandoffHealthResponse> => {
  const response = await remiApi.get<HandoffHealthResponse>('/health');
  return response.data;
};

/**
 * Get the handoff queue for an organization
 * @param organizationId - The organization ID
 * @param agentId - Optional agent's TeamMember ID to filter handoffs (NOT Firebase UID)
 * @returns List of handoffs and stats
 */
export const getHandoffQueue = async (
  organizationId: string,
  agentId?: string
): Promise<HandoffQueueResponse> => {
  try {
    const params = new URLSearchParams({ organization_id: organizationId, status: 'pending' });

    // Add agentId if provided - filters out handoffs where contact is assigned to a different agent
    if (agentId) {
      params.append('agent_id', agentId);
    }

    const response = await remiApi.get<HandoffQueueResponse>(
      `/api/v1/mobile-handoff/handoffs?${params.toString()}`
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching handoff queue:', error);
    if ((error as AxiosLike).response) {
      return {
        handoffs: [],
        count: 0,
        stats: {
          pending: 0,
          claimed: 0,
          released: 0,
          declined: 0,
          expired: 0,
          total: 0,
        },
      };
    }
    throw error;
  }
};

// =============================================================================
// SMS Handoff API
// =============================================================================

import {
  ValidateTokenResponse,
  ClaimWithNotesRequest,
  ReleaseWithNotesRequest,
  GiveBackRequest,
} from '../types/handoff.types';

/**
 * Validate a handoff token and get handoff details
 * Called when agent clicks YES link in SMS
 * @param token - The handoff token from SMS link
 * @returns Handoff details if token is valid
 */
export const validateHandoffToken = async (token: string): Promise<ValidateTokenResponse> => {
  try {
    const params = new URLSearchParams({ token });

    const response = await remiApi.get<ValidateTokenResponse>(
      `/api/v1/mobile-handoff/claim-link?${params.toString()}`
    );

    return { success: true, ...response.data };
  } catch (error) {
    console.error('Error validating handoff token:', error);
    if ((error as AxiosLike).response?.data) {
      return {
        success: false,
        error: errorReason(error, 'Invalid or expired token'),
      };
    }
    return {
      success: false,
      error: (error as AxiosLike).message || 'Failed to validate token',
    };
  }
};

/**
 * Decline a handoff using the token
 * Called when agent clicks NO link in SMS
 * @param token - The handoff token from SMS link
 * @returns Decline response
 */
export const declineHandoffByToken = async (
  token: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const params = new URLSearchParams({ token });

    const response = await remiApi.get<{ success: boolean }>(
      `/api/v1/mobile-handoff/decline-link?${params.toString()}`
    );

    return { success: true, ...response.data };
  } catch (error) {
    console.error('Error declining handoff by token:', error);
    if ((error as AxiosLike).response?.data) {
      return {
        success: false,
        error: errorReason(error, 'Failed to decline handoff'),
      };
    }
    return {
      success: false,
      error: (error as AxiosLike).message || 'Failed to decline handoff',
    };
  }
};

/**
 * Claim a handoff with notes after the call
 * @param request - Claim request with token, notes, and agent ID
 * @returns Claim response with contact ID
 */
export const claimWithNotes = async (
  request: ClaimWithNotesRequest
): Promise<{ success: boolean; contactId?: string; error?: string }> => {
  try {
    const response = await remiApi.post<{
      success: boolean;
      contactId?: string;
    }>('/api/v1/mobile-handoff/hard-claim-with-notes', request);

    return { success: true, ...response.data };
  } catch (error) {
    console.error('Error claiming with notes:', error);
    if ((error as AxiosLike).response?.data) {
      return {
        success: false,
        error: errorReason(error, 'Failed to claim lead'),
      };
    }
    return {
      success: false,
      error: (error as AxiosLike).message || 'Failed to claim lead',
    };
  }
};

/**
 * Release a handoff with notes back to the system
 * @param request - Release request with token, notes, and agent ID
 * @returns Release response
 */
export const releaseWithNotes = async (
  request: ReleaseWithNotesRequest
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await remiApi.post<{ success: boolean }>(
      '/api/v1/mobile-handoff/release-with-notes',
      request
    );

    return { success: true, ...response.data };
  } catch (error) {
    console.error('Error releasing with notes:', error);
    if ((error as AxiosLike).response?.data) {
      return {
        success: false,
        error: errorReason(error, 'Failed to release lead'),
      };
    }
    return {
      success: false,
      error: (error as AxiosLike).message || 'Failed to release lead',
    };
  }
};

/**
 * Give back a claimed lead to Remi
 * Used from contact card UI
 * @param request - Give back request with contact and handoff IDs
 * @returns Give back response
 */
export const giveBackToRemi = async (
  request: GiveBackRequest
): Promise<{ success: boolean; cadenceResumed?: boolean; error?: string }> => {
  try {
    const response = await remiApi.post<{
      success: boolean;
      cadenceResumed: boolean;
    }>('/api/v1/mobile-handoff/give-back', request);

    return { success: true, ...response.data };
  } catch (error) {
    console.error('Error giving back to Remi:', error);
    if ((error as AxiosLike).response?.data) {
      return {
        success: false,
        error: errorReason(error, 'Failed to give back to Remi'),
      };
    }
    return {
      success: false,
      error: (error as AxiosLike).message || 'Failed to give back to Remi',
    };
  }
};

/**
 * Check if a contact has an active handoff
 * @param organizationId - The organization ID
 * @param contactId - The contact ID
 * @returns Handoff status for the contact
 */
export const getHandoffStatus = async (
  organizationId: string,
  contactId: string
): Promise<HandoffStatusResponse> => {
  try {
    const params = new URLSearchParams({
      organization_id: organizationId,
      contact_id: contactId,
    });

    const response = await remiApi.get<HandoffStatusResponse>(
      `/api/v1/mobile-handoff/status?${params.toString()}`
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching handoff status:', error);
    if ((error as AxiosLike).response) {
      return {
        hasActiveHandoff: false,
        handoff: null,
      };
    }
    throw error;
  }
};

/**
 * Get handoff statistics for an organization
 * @param organizationId - The organization ID
 * @returns Handoff statistics
 */
export const getHandoffStats = async (organizationId: string): Promise<HandoffStats> => {
  try {
    const params = new URLSearchParams({ organization_id: organizationId });

    const response = await remiApi.get<HandoffStats>(
      `/api/v1/mobile-handoff/stats?${params.toString()}`
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching handoff stats:', error);
    return {
      pending: 0,
      claimed: 0,
      released: 0,
      declined: 0,
      expired: 0,
      total: 0,
    };
  }
};

/**
 * Claim a handoff as an agent
 * @param request - Claim request with organization, handoff, and agent info
 * @returns Claim response with updated handoff
 */
export const claimHandoff = async (request: ClaimHandoffRequest): Promise<ClaimHandoffResponse> => {
  try {
    const response = await remiApi.post<ClaimHandoffResponse>(
      '/api/v1/mobile-handoff/claim',
      request
    );

    return { success: true, ...response.data };
  } catch (error) {
    console.error('Error claiming handoff:', error);
    if ((error as AxiosLike).response?.data) {
      return {
        success: false,
        error: errorReason(error, 'Failed to claim handoff'),
      };
    }
    return {
      success: false,
      error: (error as AxiosLike).message || 'Failed to claim handoff',
    };
  }
};

/**
 * Decline a handoff and resume Remi (AI flywheel)
 * @param request - Decline request with organization, handoff, agent info, and reason
 * @returns Decline response
 */
export const declineHandoff = async (
  request: DeclineHandoffRequest
): Promise<DeclineHandoffResponse> => {
  try {
    const response = await remiApi.post<DeclineHandoffResponse>(
      '/api/v1/mobile-handoff/decline',
      request
    );

    return { success: true, ...response.data };
  } catch (error) {
    console.error('Error declining handoff:', error);
    if ((error as AxiosLike).response?.data) {
      return {
        success: false,
        cadenceResumed: false,
        contactId: request.handoffId,
        error: errorReason(error, 'Failed to decline handoff'),
      };
    }
    return {
      success: false,
      cadenceResumed: false,
      contactId: '',
      error: (error as AxiosLike).message || 'Failed to decline handoff',
    };
  }
};

/**
 * Evaluate a contact for handoff (mainly used internally by FollowUpFeature)
 * @param request - Evaluation request with contact and interaction data
 * @returns Evaluation response with handoff decision
 */
export const evaluateForHandoff = async (
  request: EvaluateHandoffRequest
): Promise<EvaluateHandoffResponse> => {
  try {
    const response = await remiApi.post<EvaluateHandoffResponse>(
      '/api/v1/mobile-handoff/evaluate',
      request
    );

    return response.data;
  } catch (error) {
    console.error('Error evaluating for handoff:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time handoff alerts using Server-Sent Events
 * @param organizationId - The organization ID to subscribe to
 * @param onHandoff - Callback for handoff events
 * @param onError - Optional callback for errors
 * @param onConnected - Optional callback when connection is established
 * @returns Unsubscribe function
 */
export const subscribeToHandoffAlerts = (
  organizationId: string,
  onHandoff: (event: HandoffSSEEvent) => void,
  onError?: (error: Event) => void,
  onConnected?: () => void,
  agentId?: string
): (() => void) => {
  // SPEC-2157: never put the Firebase JWT in the SSE URL — it leaks into Cloud
  // Run access logs. Instead mint a short-lived opaque stream token over a
  // normal Bearer-authenticated POST, then open the EventSource with only
  // organization_id + stream_token. The agent filter is pinned server-side at
  // mint time, so it's dropped from the connect URL too. Auth rides on the mint
  // request's Bearer header (supplied by the `remi` Axios interceptor), so no
  // token is threaded through from the caller. The cancellable lifecycle lives
  // in openHandoffAlertStream so it can be unit-tested without this env graph.
  return openHandoffAlertStream(organizationId, onHandoff, onError, onConnected, agentId, {
    mintStreamToken: (orgId, aId) =>
      remi
        .post<{
          stream_token: string;
          expires_in: number;
        }>('/api/v1/mobile-handoff/alerts/stream-token', null, {
          params: { organization_id: orgId, ...(aId ? { agent_id: aId } : {}) },
        })
        .then(({ data }) => data),
    baseUrl: getCloudRunServiceUrl('remi'),
  });
};

/**
 * Mark a handoff as converted (lead became a customer)
 * This would typically be called when a deal closes
 * @param organizationId - The organization ID
 * @param handoffId - The handoff ID
 * @param agentId - The agent ID marking the conversion
 */
export const markHandoffConverted = async (
  organizationId: string,
  handoffId: string,
  agentId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await remiApi.post<{ success: boolean }>('/api/v1/mobile-handoff/convert', {
      organizationId,
      handoffId,
      agentId,
    });

    return { success: true, ...response.data };
  } catch (error) {
    console.error('Error marking handoff as converted:', error);
    return {
      success: false,
      error: errorReason(error, 'Failed to mark as converted'),
    };
  }
};

/**
 * Cancel a handoff (manually cancel an active handoff)
 * @param organizationId - The organization ID
 * @param handoffId - The handoff ID
 * @param agentId - The agent ID cancelling the handoff
 * @param reason - Optional reason for cancellation
 */
export const cancelHandoff = async (
  organizationId: string,
  handoffId: string,
  agentId: string,
  reason?: string
): Promise<{ success: boolean; cadenceResumed?: boolean; error?: string }> => {
  try {
    const response = await remiApi.post<{
      success: boolean;
      cadenceResumed: boolean;
    }>('/api/v1/mobile-handoff/cancel', { organizationId, handoffId, agentId, reason });

    return { success: true, ...response.data };
  } catch (error) {
    console.error('Error cancelling handoff:', error);
    return {
      success: false,
      error: errorReason(error, 'Failed to cancel handoff'),
    };
  }
};

// =============================================================================
// Flywheel Management API
// =============================================================================

export interface OptOutRequest {
  organizationId: string;
  contactId: string;
  agentId: string;
  reason?: string;
}

export interface OptOutResponse {
  success: boolean;
  contactId: string;
  flywheelStatus: 'opted_out' | 'active';
  error?: string;
}

export interface OptInRequest {
  organizationId: string;
  contactId: string;
  agentId: string;
}

export interface OptInResponse {
  success: boolean;
  contactId: string;
  flywheelStatus: 'opted_out' | 'active';
  error?: string;
}

/**
 * Check if a contact is opted out of the flywheel
 * @param organizationId - The organization ID
 * @param contactId - The contact ID
 * @returns Flywheel status for the contact
 */
export const getFlywheelStatus = async (
  organizationId: string,
  contactId: string
): Promise<{ isOptedOut: boolean; optedOutAt?: string; optedOutBy?: string }> => {
  try {
    const params = new URLSearchParams({ organization_id: organizationId, contact_id: contactId });

    const response = await remiApi.get<{
      isOptedOut: boolean;
      optedOutAt?: string;
      optedOutBy?: string;
    }>(`/api/v1/mobile-handoff/flywheel-status?${params.toString()}`);

    return response.data;
  } catch (error) {
    console.error('Error fetching flywheel status:', error);
    return { isOptedOut: false };
  }
};

/**
 * Opt a contact out of the AI flywheel
 * Stops all automated follow-ups for this contact
 * @param request - Opt-out request with organization, contact, and agent info
 * @returns Opt-out response
 */
export const optOutOfFlywheel = async (request: OptOutRequest): Promise<OptOutResponse> => {
  try {
    const response = await remiApi.post<OptOutResponse>('/api/v1/mobile-handoff/opt-out', request);

    return response.data;
  } catch (error) {
    console.error('Error opting out of flywheel:', error);
    if ((error as AxiosLike).response?.data) {
      return {
        success: false,
        contactId: request.contactId,
        flywheelStatus: 'active',
        error: errorReason(error, 'Failed to opt out of flywheel'),
      };
    }
    return {
      success: false,
      contactId: request.contactId,
      flywheelStatus: 'active',
      error: (error as AxiosLike).message || 'Failed to opt out of flywheel',
    };
  }
};

/**
 * Opt a contact back into the AI flywheel
 * Re-enables automated follow-ups for this contact
 * @param request - Opt-in request with organization, contact, and agent info
 * @returns Opt-in response
 */
export const optInToFlywheel = async (request: OptInRequest): Promise<OptInResponse> => {
  try {
    const response = await remiApi.post<OptInResponse>('/api/v1/mobile-handoff/opt-in', request);

    return response.data;
  } catch (error) {
    console.error('Error opting into flywheel:', error);
    if ((error as AxiosLike).response?.data) {
      return {
        success: false,
        contactId: request.contactId,
        flywheelStatus: 'opted_out',
        error: errorReason(error, 'Failed to opt into flywheel'),
      };
    }
    return {
      success: false,
      contactId: request.contactId,
      flywheelStatus: 'opted_out',
      error: (error as AxiosLike).message || 'Failed to opt into flywheel',
    };
  }
};

// =============================================================================
// Handoff History API (for Dashboard Stats Modal)
// =============================================================================

/**
 * A single lifecycle event in a handoff's timeline (SPEC-2253). Present on
 * every handoff object as `history` (oldest-first); `[]` on legacy rows.
 */
export interface HandoffLifecycleEvent {
  event: string; // created | claimed | released | converted | hard_expired | claim_link_used | ...
  at: string; // ISO timestamp
  actorType: 'agent' | 'link' | 'system' | 'scheduler';
  actorAgentId?: string;
  actorAgentName?: string;
  fromStatus?: string;
  toStatus?: string;
  reason?: string;
  notes?: string;
  meta?: Record<string, unknown>;
}

export interface HandoffHistoryItem {
  handoffId: string;
  contactId?: string;
  history?: HandoffLifecycleEvent[];
  contactName: string | null;
  contactPhone: string | null;
  contactEmail?: string | null;
  hotnessScore: number;
  hotnessLevel?: 'urgent' | 'hot' | 'high' | 'warm' | 'cool' | 'cold';
  signals?: HotSignals;
  reasons?: unknown[];
  conversationSummary?: string | null;
  handoffSummary?: string | null;
  status: HandoffStatus;
  assignedAgentId: string | null;
  assignedAgentName: string | null;
  agentNotes?: string | null;
  createdAt: string;
  claimedAt: string | null;
  // createdAt + 72h: when the hard-expire cron drops the row from the queue.
  appClaimExpiresAt?: string | null;
  handoffLane?: HandoffLane;
  channelOfOrigin?: ChannelOfOrigin;
  location?: HandoffLocation | null;
}

export interface HandoffHistoryResponse {
  handoffs: HandoffHistoryItem[];
  count: number; // Results returned in this page
  totalCount: number; // Total matching records
  statusCounts: HandoffStatusCounts;
  filters: {
    startDate: string | null;
    endDate: string | null;
    status: string;
    limit: number;
    offset: number;
  };
}

export interface HandoffHistoryParams {
  organizationId: string;
  agentId?: string; // TeamMember ID for filtering by agent assignment
  startDate?: string;
  endDate?: string;
  status?: HandoffStatus | 'all';
  limit?: number;
  offset?: number;
}

/**
 * Get handoff history for an organization
 * Used for dashboard stats modal to show list of handoffs
 * @param params - Query parameters for filtering
 * @returns List of handoffs with count and filters applied
 */
export const getHandoffHistory = async (
  params: HandoffHistoryParams
): Promise<HandoffHistoryResponse> => {
  try {
    const queryParams = new URLSearchParams({
      organization_id: params.organizationId,
    });

    if (params.agentId) {
      queryParams.append('agent_id', params.agentId);
    }
    if (params.startDate) {
      queryParams.append('start_date', params.startDate);
    }
    if (params.endDate) {
      queryParams.append('end_date', params.endDate);
    }
    if (params.status) {
      queryParams.append('status', params.status);
    }
    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params.offset) {
      queryParams.append('offset', params.offset.toString());
    }

    const response = await remiApi.get<Record<string, unknown>>(
      `/api/v1/mobile-handoff/history?${queryParams.toString()}`
    );

    const raw = response.data;
    // Normalize snake_case API response to frontend camelCase shape
    const rows = (raw.handoffs as HandoffHistoryItem[]) || [];
    return {
      handoffs: rows.map((row) => ({ ...row, status: normalizeHandoffStatus(row.status) })),
      count: (raw.count as number) || 0,
      totalCount: (raw.total_count as number) ?? (raw.totalCount as number) ?? 0,
      statusCounts: normalizeStatusCounts(raw.status_counts ?? raw.statusCounts),
      filters: {
        startDate: params.startDate || null,
        endDate: params.endDate || null,
        status: params.status || 'all',
        limit: params.limit || 50,
        offset: params.offset || 0,
      },
    };
  } catch (error) {
    console.error('Error fetching handoff history:', error);
    return {
      handoffs: [],
      count: 0,
      totalCount: 0,
      statusCounts: EMPTY_STATUS_COUNTS,
      filters: {
        startDate: params.startDate || null,
        endDate: params.endDate || null,
        status: params.status || 'all',
        limit: params.limit || 50,
        offset: params.offset || 0,
      },
    };
  }
};

// =============================================================================
// Handoff Details API (for expanded card with contact activity)
// =============================================================================

export interface HandoffDetailCall {
  callId: string;
  outcome: string;
  summary: string;
  duration: number;
  createdAt: string;
  recordingUrl?: string;
  inbound?: boolean;
}

export interface HandoffDetailEmail {
  id: string;
  subject: string;
  sentAt: string;
  opened: boolean;
  clicked: boolean;
}

export interface HandoffDetailWebActivity {
  id: string;
  deviceType: 'mobile' | 'desktop' | 'tablet';
  eventType: string;
  pageUrl: string;
  timestamp: string;
  properties?: {
    address?: string;
    city?: string;
    state?: string;
    searchQuery?: string;
  };
}

export interface HandoffDetailResponse {
  handoff: {
    handoffId: string;
    contactId?: string;
    contactName: string | null;
    contactPhone: string | null;
    contactEmail?: string | null;
    hotnessScore: number;
    hotnessLevel?: string;
    reasons?: string[];
    status: string;
    conversationSummary: string | null;
    handoffSummary?: string | null;
    assignedAgentId?: string | null;
    assignedAgentName?: string | null;
    createdAt?: string;
    claimedAt?: string | null;
  };
  contactActivity: {
    calls: HandoffDetailCall[];
    emails: HandoffDetailEmail[];
    websiteActivity: HandoffDetailWebActivity[];
  };
  claimEligibility: {
    canClaim: boolean;
    reason: string | null;
  };
}

/**
 * Get detailed handoff information including contact activity
 * Called when user expands a handoff card to see recent activity
 * @param handoffId - The handoff ID
 * @param organizationId - The organization ID
 * @param agentId - Optional agent ID for claim eligibility check
 * @returns Detailed handoff information with contact activity
 */
export const getHandoffDetailWithActivity = async (
  handoffId: string,
  organizationId: string,
  agentId?: string
): Promise<HandoffDetailResponse | null> => {
  try {
    const queryParams = new URLSearchParams({ organization_id: organizationId });

    if (agentId) {
      queryParams.append('agent_id', agentId);
    }

    const response = await remiApi.get<HandoffDetailResponse>(
      `/api/v1/mobile-handoff/handoffs/${handoffId}?${queryParams.toString()}`
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching handoff details:', error);
    return null;
  }
};
